import {
  IHealthScribeService,
  IHealthScribeRepository,
  IHealthScribeWebSocketProvider,
  IHealthScribeAudioProvider,
  IStartRecordingRequest,
  IStartRecordingResponse,
  IStopRecordingRequest,
  IStopRecordingResponse,
  ITranscriptionSaveRequest,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  ICompleteTranscriptionRequest,
  ICompleteTranscriptionResponse,
  IHealthScribeSession,
  IAudioChunk,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
} from "../ports/HealthScribe";
import { IS3UploadProvider } from "../../adapters/driven/providers/healthScribeS3UploadProvider";

export const HealthScribeServiceFactory = ({
  healthScribeRepository,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  s3UploadProvider,
}: {
  healthScribeRepository: IHealthScribeRepository;
  healthScribeWebSocketProvider: IHealthScribeWebSocketProvider;
  healthScribeAudioProvider: IHealthScribeAudioProvider;
  s3UploadProvider: IS3UploadProvider;
}): IHealthScribeService => {
  let currentSession: IHealthScribeSession | null = null;

  const startSession = async (
    request: IStartRecordingRequest
  ): Promise<IControllerPromiseResponse<IStartRecordingResponse>> => {
    try {
      console.log("[HealthScribe Service] Starting session:", request);

      // Start the recording session
      const response = await healthScribeRepository.startRecording(request);

      // Create session object
      currentSession = {
        sessionId: response.session_id,
        patientId: request.patient_id,
        clinicId: request.clinic_id,
        patientName: "", // Not provided in new API structure
        visitType: request.visit_type,
        startTime: new Date().toISOString(),
        status: "active",
        transcription: [],
      };

      console.log(
        "[HealthScribe Service] Session started successfully:",
        currentSession
      );
      return { success: true, data: response };
    } catch (error) {
      console.error("[HealthScribe Service] Failed to start session:", error);
      return {
        success: false,
        error: `Failed to start HealthScribe session: ${error}`,
      };
    }
  };

  const stopSession = async (
    request: IStopRecordingRequest
  ): Promise<IControllerPromiseResponse<IStopRecordingResponse>> => {
    try {
      console.log("[HealthScribe Service] Stopping session:", request);

      if (!currentSession) {
        throw new Error("No active session to stop");
      }

      // Audio recording should already be stopped by the controller
      console.log(
        "[HealthScribe Service] Session stopping (audio already stopped by controller)"
      );

      // Disconnect WebSocket first to prevent new messages
      await disconnectLiveTranscription();

      // Save any remaining transcription messages
      const session = currentSession; // Capture in local variable to satisfy TypeScript
      let narrative = "";
      if (session?.transcription && session.transcription.length > 0) {
        const messagesToSave: ITranscriptionSaveRequest = {
          clinic_id: session.clinicId as number,
          visit_type: session.visitType,
          transcriptions: session.transcription.map((msg) => ({
            session_id: session.sessionId,
            clinic_id: session.clinicId as number,
            speaker: msg.speaker,
            message: msg.message,
            confidence: msg.confidence || 0.0,
            is_final: !msg.isPartial,
          })),
          session_id: session.sessionId,
          // Include context data from the request if provided
          context: request.context,
          is_completed: request.is_completed,
        };
        try {
          const res = await healthScribeRepository.batchSaveTranscriptions(
            messagesToSave
          );
          if (res && res.narrative) {
            narrative = res.narrative;
          }

          console.log(
            "[HealthScribe Service] Saved complete transcription messages:",
            messagesToSave?.transcriptions?.length
          );
        } catch (error) {
          console.error(
            "[HealthScribe Service] Failed to save complete transcriptions:",
            error
          );
        }
      }

      // Stop the recording session
      const response = await healthScribeRepository.stopRecording(request);

      // Reset current session
      currentSession = null;

      console.log(
        "[HealthScribe Service] Session stopped successfully:",
        response
      );
      return { success: true, data: { ...response, narrative } };
    } catch (error) {
      console.error("[HealthScribe Service] Failed to stop session:", error);

      return {
        success: false,
        error: `Failed to stop HealthScribe session: ${error}`,
      };
    }
  };

  const connectLiveTranscription = async (
    sessionId: string,
    websocketUrl?: string
  ): Promise<IControllerPromiseResponse<void>> => {
    try {
      console.log(
        "[HealthScribe Service] Connecting live transcription for session:",
        sessionId,
        "WebSocket URL:",
        websocketUrl
      );

      if (!currentSession || currentSession.sessionId !== sessionId) {
        throw new Error("Invalid session ID or no active session");
      }

      // Store the existing transcription handler
      const existingHandler = healthScribeWebSocketProvider.onTranscription;

      // Set up transcription handler before connecting
      healthScribeWebSocketProvider.onTranscription = async (message) => {
        try {
          // Update current session transcription array
          if (currentSession) {
            currentSession.transcription = currentSession.transcription || [];
            currentSession.transcription.push(message);
          }

          // Call the existing handler if it exists
          if (existingHandler) {
            existingHandler(message);
          }
        } catch (error) {
          console.error(
            "[HealthScribe Service] Failed to save transcription message:",
            error
          );
          // Still call the existing handler even if save fails
          if (existingHandler) {
            existingHandler(message);
          }
        }
      };

      // Connect to WebSocket with the provided URL
      if (!websocketUrl) {
        throw new Error("WebSocket URL is required for connection");
      }
      await healthScribeWebSocketProvider.connect(
        sessionId,
        currentSession.patientId as number,
        websocketUrl
      );
      console.log(
        "[HealthScribe Service] Live transcription connected successfully"
      );
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to connect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to connect live transcription: ${error}`,
      };
    }
  };

  const disconnectLiveTranscription = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Service] Disconnecting live transcription");

      await healthScribeWebSocketProvider.disconnect();
      console.log(
        "[HealthScribe Service] Live transcription disconnected successfully"
      );
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to disconnect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to disconnect live transcription: ${error}`,
      };
    }
  };

  const requestMicrophonePermission = async (): Promise<
    IControllerPromiseResponse<{ granted: boolean }>
  > => {
    try {
      console.log("[HealthScribe Service] Requesting microphone permission");
      const result =
        await healthScribeAudioProvider.requestMicrophonePermission();
      console.log(
        "[HealthScribe Service] Microphone permission result:",
        result
      );
      return { success: true, data: result };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to request microphone permission:",
        error
      );
      return {
        success: false,
        error: `Failed to request microphone permission: ${error}`,
      };
    }
  };

  const startAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Service] Starting audio recording");

      if (!currentSession) {
        throw new Error("No active session. Start a session first.");
      }

      // Start audio recording
      await healthScribeAudioProvider.startRecording();

      // Set up audio chunk handler to send to WebSocket for live transcription
      healthScribeAudioProvider.onAudioChunk = async (chunk: IAudioChunk) => {
        try {
          // Send to WebSocket for live transcription
          if (healthScribeWebSocketProvider.isConnected()) {
            await healthScribeWebSocketProvider.sendAudioChunk(chunk);
          }
        } catch (error) {
          console.error(
            "[HealthScribe Service] Failed to process audio chunk:",
            error
          );
        }
      };

      console.log(
        "[HealthScribe Service] Audio recording started successfully"
      );
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to start audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to start audio recording: ${error}`,
      };
    }
  };

  const stopAudioRecording = async (): Promise<
    IControllerPromiseResponse<{
      audioBlob: Blob;
      duration: number;
    }>
  > => {
    try {
      console.log("[HealthScribe Service] Stopping audio recording");

      // Stop audio recording and get the final audio data
      const audioResult = await healthScribeAudioProvider.stopRecording();

      console.log(
        "[HealthScribe Service] Audio recording stopped successfully"
      );
      return { success: true, data: audioResult };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to stop audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to stop audio recording: ${error}`,
      };
    }
  };

  const pauseAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Service] Pausing audio recording");
      await healthScribeAudioProvider.pauseRecording();
      console.log("[HealthScribe Service] Audio recording paused successfully");
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to pause audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to pause audio recording: ${error}`,
      };
    }
  };

  const resumeAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Service] Resuming audio recording");
      await healthScribeAudioProvider.resumeRecording();
      console.log(
        "[HealthScribe Service] Audio recording resumed successfully"
      );
      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to resume audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to resume audio recording: ${error}`,
      };
    }
  };

  const getSession = (): IHealthScribeSession | null => {
    return currentSession;
  };

  const populateSessionFromTranscription = (transcriptionData: {
    session_id: string;
    patient_id: string | number;
    clinic_id?: number;
    patient_name?: string;
    visit_type?: string;
    created_at?: string;
    transcription?: string;
    status?: "active" | "paused" | "completed" | "failed";
  }): IHealthScribeSession => {
    // Parse transcription data if it's a string
    const parsedTranscription = transcriptionData.transcription
      ? JSON.parse(transcriptionData.transcription)
      : [];

    // Create session object from transcription data
    const session: IHealthScribeSession = {
      sessionId: transcriptionData.session_id,
      patientId: transcriptionData.patient_id,
      clinicId: transcriptionData.clinic_id || 0,
      patientName: transcriptionData.patient_name,
      visitType: transcriptionData.visit_type || "START_OF_CARE",
      startTime: transcriptionData.created_at || new Date().toISOString(),
      status: transcriptionData.status || "completed",
      transcription: parsedTranscription,
    };

    // Set as current session
    currentSession = session;

    console.log(
      "[HealthScribe Service] Session populated from transcription:",
      currentSession
    );

    return session;
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IControllerPromiseResponse<IS3UploadCompleteAudioResponse>> => {
    try {
      console.log("[HealthScribe Service] Uploading complete audio:", {
        sessionId: request.session_id,
        clinicId: request.clinic_id,
        filename: request.filename,
        contentType: request.content_type,
        audioFileSize: request.audio_file.size,
      });

      const response = await healthScribeRepository.uploadCompleteAudio(
        request
      );

      console.log(
        "[HealthScribe Service] Audio upload completed successfully:",
        response
      );

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to upload complete audio:",
        error
      );
      return {
        success: false,
        error: `Failed to upload complete audio: ${error}`,
      };
    }
  };

  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IControllerPromiseResponse<IListTranscriptionsResponse>> => {
    try {
      const response = await healthScribeRepository.listTranscriptions(request);

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to list transcriptions:",
        error
      );
      return {
        success: false,
        error: `Failed to list transcriptions: ${error}`,
      };
    }
  };

  const completeTranscriptionGenerateNarrative = async (
    request: ICompleteTranscriptionRequest
  ): Promise<IControllerPromiseResponse<ICompleteTranscriptionResponse>> => {
    try {
      console.log(
        "[HealthScribe Service] Completing transcription generate narrative:",
        {
          transcriptionId: request.transcription_id,
          clinicId: request.clinic_id,
          contextLength: request.context.length,
        }
      );

      const response =
        await healthScribeRepository.completeTranscriptionGenerateNarrative(
          request
        );

      console.log(
        "[HealthScribe Service] Transcription narrative generated successfully:",
        {
          success: response.success,
          message: response.message,
          generatedAt: response.generated_at,
          narrativeLength: response.narrative.length,
        }
      );

      return { success: true, data: response };
    } catch (error) {
      console.error(
        "[HealthScribe Service] Failed to complete transcription generate narrative:",
        error
      );
      return {
        success: false,
        error: `Failed to complete transcription generate narrative: ${error}`,
      };
    }
  };

  return {
    startSession,
    stopSession,
    connectLiveTranscription,
    disconnectLiveTranscription,
    requestMicrophonePermission,
    startAudioRecording,
    stopAudioRecording,
    pauseAudioRecording,
    resumeAudioRecording,
    getSession,
    populateSessionFromTranscription,
    uploadCompleteAudio,
    listTranscriptions,
    completeTranscriptionGenerateNarrative,
  };
};
